import { useRef, useState } from "react";
import { Upload, Link as LinkIcon, Loader2, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { fileUrl } from "../../lib/cta";
import { toast } from "sonner";

const MAX_SIZE_MB = 10;
const DEFAULT_ALLOWED_MIME = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif", "image/avif", "image/svg+xml", "image/x-icon", "image/vnd.microsoft.icon"];
const DEFAULT_ALLOWED_EXT_RE = /\.(jpe?g|png|webp|gif|avif|svg|ico)$/i;
const DEFAULT_ALLOWED_LABEL = "JPG, PNG, WebP, GIF, AVIF, SVG, ICO";

export default function ImageUploadField({ value, onChange, testId, label, acceptTypes, acceptExtRegex, acceptLabel }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [justUploaded, setJustUploaded] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const allowedMime = acceptTypes || DEFAULT_ALLOWED_MIME;
  const allowedRegex = acceptExtRegex || DEFAULT_ALLOWED_EXT_RE;
  const allowedLabel = acceptLabel || DEFAULT_ALLOWED_LABEL;

  const handleFile = async (file) => {
    if (!file) return;
    if (!allowedMime.includes(file.type) && !file.name.match(allowedRegex)) {
      toast.error(`Formato não suportado. Use: ${allowedLabel}`);
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      const mb = (file.size / 1024 / 1024).toFixed(1);
      toast.error(`Arquivo muito grande (${mb}MB). Máximo permitido: ${MAX_SIZE_MB}MB`);
      return;
    }
    setUploading(true);
    setJustUploaded(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      setJustUploaded(true);
      toast.success("Imagem enviada! Clique em SALVAR no topo para aplicar.");
      setTimeout(() => setJustUploaded(false), 4000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const onPick = () => ref.current?.click();
  const onFile = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const preview = fileUrl(value);

  return (
    <div className="space-y-2">
      {label && <span className="block text-xs tracking-[0.2em] uppercase text-onyx/60">{label}</span>}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`flex items-start gap-3 transition-colors ${dragOver ? "ring-2 ring-gold p-2 -m-2" : ""}`}
      >
        {preview ? (
          <img src={preview} alt="" className="w-16 h-16 object-cover border border-border flex-shrink-0" />
        ) : (
          <div className="w-16 h-16 bg-muted flex items-center justify-center border border-border flex-shrink-0 text-xs text-onyx/40">—</div>
        )}
        <div className="flex-1 space-y-2">
          <div className="relative">
            <LinkIcon size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-onyx/40" />
            <input
              data-testid={testId ? `${testId}-url` : undefined}
              type="text"
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="https://… ou /api/files/abc.jpg"
              className="w-full pl-8 pr-3 py-2 border border-border bg-white text-onyx focus:outline-none focus:border-gold text-sm"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={onPick}
              data-testid={testId ? `${testId}-upload` : undefined}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border border-onyx hover:bg-onyx hover:text-alabaster transition-colors disabled:opacity-50"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {uploading ? "Enviando…" : "Enviar arquivo"}
            </button>
            <input
              ref={ref}
              type="file"
              accept={(acceptTypes || DEFAULT_ALLOWED_MIME).join(",") + ",.jpg,.jpeg,.png,.webp,.gif,.avif,.svg,.ico"}
              onChange={onFile}
              className="hidden"
              data-testid={testId ? `${testId}-file-input` : undefined}
            />
            {justUploaded && (
              <span className="inline-flex items-center gap-1 text-[10px] tracking-wider text-gold">
                <Check size={12} /> Enviado — lembre de clicar em SALVAR
              </span>
            )}
            <span className="text-[10px] text-onyx/40">
              Máx {MAX_SIZE_MB}MB · {allowedLabel.toLowerCase()} · arraste e solte também
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
